<script lang="ts" setup>
import { computed, toRef } from 'vue';
import { useFieldArray } from 'vee-validate';
import { EvaluationParameterScopeDto, ParameterCombinationTableDto } from '@/api/generator';
import { FormMode } from '@/components/forms/types';
import { createEvaluationScope } from '../utils';
import EvaluationScope, { ScopeDisplayOptions } from './EvaluationScope.vue';
import FormAddButton from './FormAddButton.vue';
import { SortableOptions, SortableEvent } from 'sortablejs';
import { Sortable } from 'sortablejs-vue3';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles';
import { getAssignedScopesByCombination } from '@/components/evaluation/scopes/combinationMatch';

const props = defineProps<
  {
    name: string; // evaluationScopes
    mode: FormMode;
    targetSystemId?: string;
    combinationTable?: ParameterCombinationTableDto;
  } & ScopeDisplayOptions
>();

const hideDisabled = computed(
  () =>
    props.sorting === 'disabled' && props.toggle === 'disabled' && props.editable === 'disabled',
);

const {
  fields: scopes,
  push,
  remove,
  move,
} = useFieldArray<EvaluationParameterScopeDto>(toRef(() => props.name));

function addScope() {
  push(createEvaluationScope(scopes.value.map((scope) => scope.value)));
}

const combinations = computed(() => props.combinationTable?.combinations ?? []);

const assignedScopes = computed(() =>
  getAssignedScopesByCombination(
    scopes.value.map((scope) => scope.value),
    combinations.value,
  ),
);

const unmatchedCombinations = computed(() => {
  if (!scopes.value.some((scope) => scope.value.isEnabled)) return [];

  return combinations.value.filter(
    (combination) => combination.isEnabled && !assignedScopes.value.get(combination)?.length,
  );
});

const { getTitle } = useTaskStepTitle(combinations);

const sortableOptions = computed<SortableOptions>(() => ({
  handle: '.scopeHandle',
  disabled: props.mode === 'details' && props.sorting === 'enabled',
  scroll: true,
  scrollSensitivity: 300,
  forceFallback: true,
}));

function sortingEndHandler(e: SortableEvent) {
  if (e.oldIndex !== undefined && e.newIndex !== undefined) {
    move(e.oldIndex, e.newIndex);
  }
}
</script>

<template>
  <v-card class="container-level-1">
    <v-card-item>
      <v-card-title class="d-flex align-center">
        <h5>{{ $t('Evaluation_other') }}</h5>
        <VSpacer />
        <FormAddButton
          v-if="mode !== 'details' && editable === 'enabled'"
          :label="'AddEvaluationScope'"
          @click="addScope"
        />
      </v-card-title>
    </v-card-item>

    <v-card-text>
      <v-alert
        v-if="unmatchedCombinations.length"
        type="warning"
        variant="tonal"
        :title="$t('UnmatchedCombinations')"
        class="mb-4"
      >
        <TaskStepTitle
          v-for="(combination, index) in unmatchedCombinations"
          :key="combination.hash ?? index"
          :combination="getTitle(combination)"
        />
      </v-alert>

      <v-expansion-panels eager multiple :rounded="true" class="mb-1">
        <Sortable
          class="w-100 ga-2"
          :list="scopes"
          :options="sortableOptions"
          itemKey="key"
          @end="sortingEndHandler"
        >
          <template #item="{ index }">
            <EvaluationScope
              v-if="scopes[index].value.isEnabled || !hideDisabled"
              :name="`${props.name}[${index}]`"
              :mode="mode"
              :targetSystemId="targetSystemId"
              :combinationTable="combinationTable"
              :onDelete="mode !== 'details' ? () => remove(index) : undefined"
              :toggle="toggle"
              :sorting="sorting"
              :editable="editable"
              :evaluations="evaluations"
            />
          </template>
        </Sortable>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>
